import { useEffect, useRef, useState } from 'react';
import { Drawer, Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ReactMarkdown from 'react-markdown';

import { colors, typography, shadows, gradient } from '../theme/designTheme';
import { useAgentConversation, useCaseBrief } from '../hooks/useAgentChat';
import { streamAgentChat } from '../api/agentChat';

// ── Message primitives ───────────────────────────────────────────────

const AgentMessage = ({ content, streaming }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxWidth: '100%' }}>
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1, fontSize: '10.5px',
      letterSpacing: '0.1em', textTransform: 'uppercase', color: colors.textSecondary, fontWeight: 600,
    }}>
      <Box sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5,
        p: '2px 6px', bgcolor: colors.primaryContainer, color: colors.onPrimaryContainer,
        borderRadius: '4px', fontWeight: 700, letterSpacing: '0.08em',
      }}>
        <AutoAwesomeIcon sx={{
          fontSize: 9,
          animation: 'sparkle-pulse 2s ease-in-out infinite',
          '@keyframes sparkle-pulse': {
            '0%,100%': { opacity: 0.7, transform: 'scale(1)' },
            '50%': { opacity: 1, transform: 'scale(1.15)' },
          },
        }} />
        Assistant
      </Box>
      {streaming && (
        <Box component="span" sx={{ fontWeight: 500, color: colors.outlineVariant, letterSpacing: '0.04em' }}>
          · streaming
        </Box>
      )}
    </Box>
    <Box sx={{
      fontFamily: typography.fontFamilySerif, fontSize: '13.5px', lineHeight: 1.6,
      color: colors.textPrimary,
      '& p': { m: 0, mb: 1.25 }, '& p:last-child': { mb: 0 },
      '& strong': { fontWeight: 600 },
      '& ul, & ol': { pl: 2.5, mb: 1.25 },
      '& li': { mb: 0.5 },
      '& code': {
        fontFamily: typography.fontFamilyMono, fontSize: '12px',
        bgcolor: colors.canvas, px: 0.6, py: '1px', borderRadius: '3px',
      },
    }}>
      {content
        ? <ReactMarkdown>{content}</ReactMarkdown>
        : <Box component="span" sx={{ color: colors.textSecondary, fontStyle: 'italic' }}>Thinking…</Box>
      }
    </Box>
  </Box>
);

const UserMessage = ({ content }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'flex-end' }}>
    <Box sx={{ fontSize: '10.5px', color: colors.outlineVariant, letterSpacing: '0.04em' }}>
      You
    </Box>
    <Box sx={{
      bgcolor: colors.primary, color: '#fff',
      p: '10px 14px', borderRadius: '12px 12px 4px 12px',
      maxWidth: '85%', fontSize: '13px', lineHeight: 1.5,
      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
    }}>
      {content}
    </Box>
  </Box>
);

const TypingIndicator = () => (
  <Box sx={{
    display: 'flex', gap: 0.5, p: '10px 14px', bgcolor: colors.canvas, width: 'fit-content',
    borderRadius: '12px 12px 12px 4px',
    '& span': {
      width: 6, height: 6, borderRadius: '50%', bgcolor: colors.outlineVariant,
      animation: 'bounce 1.4s ease-in-out infinite',
    },
    '& span:nth-of-type(2)': { animationDelay: '0.15s' },
    '& span:nth-of-type(3)': { animationDelay: '0.3s' },
    '@keyframes bounce': {
      '0%, 60%, 100%': { transform: 'translateY(0)', opacity: 0.6 },
      '30%':           { transform: 'translateY(-4px)', opacity: 1 },
    },
  }}>
    <span /><span /><span />
  </Box>
);

// ── Empty state ──────────────────────────────────────────────────────

const EmptyState = ({ suggestions, onPick }) => (
  <Box sx={{
    display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start', py: 1,
  }}>
    <Box>
      <Typography sx={{
        fontFamily: typography.fontFamilyDisplay, fontWeight: 700, fontSize: '14.5px',
        letterSpacing: '-0.005em', color: colors.textPrimary,
      }}>
        Ask anything about this case
      </Typography>
      <Typography sx={{ fontSize: '12.5px', color: colors.textSecondary, mt: 0.5, lineHeight: 1.5 }}>
        I have access to the case brief — entities, transactions, red flags, and verification results.
        I'll only answer from what's there.
      </Typography>
    </Box>
    {suggestions?.length > 0 && (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, width: '100%' }}>
        {suggestions.map((s, i) => (
          <Box
            key={i}
            onClick={() => onPick(s)}
            sx={{
              p: '10px 14px', bgcolor: colors.canvas, borderRadius: '10px',
              fontFamily: typography.fontFamilySerif, fontSize: '13px', color: colors.textPrimary,
              cursor: 'pointer', transition: 'background 0.12s',
              '&:hover': { bgcolor: colors.primaryContainer, color: colors.onPrimaryContainer },
            }}
          >
            “{s}”
          </Box>
        ))}
      </Box>
    )}
  </Box>
);

// ── Composer ─────────────────────────────────────────────────────────

const Composer = ({ onSend, busy, onStop }) => {
  const [draft, setDraft] = useState('');
  const taRef = useRef(null);

  const grow = () => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  };

  const send = () => {
    const t = draft.trim();
    if (!t || busy) return;
    onSend(t);
    setDraft('');
    if (taRef.current) taRef.current.style.height = 'auto';
  };

  return (
    <Box sx={{
      borderTop: `1px solid ${colors.canvas}`,
      p: '12px 22px 16px', display: 'flex', flexDirection: 'column', gap: 1.25,
      flexShrink: 0, bgcolor: colors.surface,
    }}>
      <Box sx={{
        display: 'flex', alignItems: 'flex-end', gap: 1,
        bgcolor: colors.canvas, borderRadius: '12px', p: '6px 6px 6px 14px',
        transition: 'box-shadow 0.15s, background 0.15s',
        '&:focus-within': { bgcolor: colors.surface, boxShadow: `0 0 0 2px ${colors.primaryContainer}` },
      }}>
        <Box
          component="textarea"
          ref={taRef}
          value={draft}
          onChange={(e) => { setDraft(e.target.value); grow(); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder="Ask about this case…"
          rows={1}
          disabled={busy}
          sx={{
            flex: 1, border: 0, outline: 0, background: 'transparent', resize: 'none',
            fontFamily: typography.fontFamily, fontSize: '13px', fontWeight: 400, lineHeight: 1.5,
            color: colors.textPrimary,
            p: '8px 0', maxHeight: 120, minHeight: 24,
            '&::placeholder': { color: colors.outlineVariant },
            '&:disabled': { cursor: 'not-allowed' },
          }}
        />
        <Box
          component="button"
          onClick={busy ? onStop : send}
          disabled={!busy && !draft.trim()}
          sx={{
            width: 32, height: 32, border: 0, borderRadius: '8px',
            background: busy ? colors.surfaceContainer : gradient.primary,
            color: busy ? colors.textPrimary : '#fff', cursor: 'pointer',
            display: 'grid', placeItems: 'center',
            boxShadow: busy ? 'none' : '0 2px 8px rgba(0,90,196,0.25)',
            transition: 'transform 0.08s, filter 0.15s',
            '&:hover': { filter: 'brightness(1.08)' },
            '&:active': { transform: 'translateY(1px)' },
            '&:disabled': { opacity: 0.4, cursor: 'default' },
          }}
          title={busy ? 'Stop' : 'Send'}
        >
          {busy ? (
            <Box component="svg" sx={{ width: 12, height: 12 }} viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="6" width="12" height="12" rx="2" />
            </Box>
          ) : (
            <SendIcon sx={{ fontSize: 16 }} />
          )}
        </Box>
      </Box>

      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        fontSize: '10.5px', color: colors.outlineVariant, px: 0.25,
      }}>
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <Box component="svg" sx={{ width: 11, height: 11 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}>
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </Box>
          Responses are logged to the case audit trail
        </Box>
        <Box component="span">
          <Box component="span" sx={{
            fontFamily: typography.fontFamilyMono, p: '1px 5px',
            bgcolor: colors.canvas, borderRadius: '3px', color: colors.textSecondary, fontWeight: 500,
          }}>
            ↵
          </Box>
          {' '}to send
        </Box>
      </Box>
    </Box>
  );
};

// ── Main ─────────────────────────────────────────────────────────────

const AgentChatPanel = ({ open, onClose, caseNumber }) => {
  const { data } = useAgentConversation(caseNumber, open);
  const { data: briefData, isLoading: briefLoading } = useCaseBrief(caseNumber, open);
  const suggestions = data?.suggested_prompts ?? [];
  const caseBrief = briefData?.markdown;

  // Ephemeral thread state (not persisted)
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const abortRef = useRef(null);
  const scrollRef = useRef(null);

  // Clear on panel close
  useEffect(() => {
    if (!open) {
      if (abortRef.current) abortRef.current.abort();
      setMessages([]);
      setStreaming(false);
    }
  }, [open]);

  // Autoscroll on new tokens
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streaming]);

  const send = async (text) => {
    if (!text.trim() || streaming) return;

    // Snapshot completed turns BEFORE inserting the new placeholder.
    const history = messages
      .filter((m) => (m.role === 'user' || m.role === 'assistant') && m.content)
      .map((m) => ({ role: m.role, content: m.content }));

    const userMsg = { id: `u-${Date.now()}`, role: 'user', content: text.trim() };
    const asstId = `a-${Date.now()}`;
    setMessages((prev) => [...prev, userMsg, { id: asstId, role: 'assistant', content: '' }]);
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await streamAgentChat(
        caseNumber,
        { message: text.trim(), brief: caseBrief, history },
        {
          signal: controller.signal,
          onDelta: (delta) => {
            setMessages((prev) => prev.map((m) =>
              m.id === asstId ? { ...m, content: m.content + delta } : m
            ));
          },
        },
      );
    } catch (err) {
      if (err.name === 'AbortError') {
        // User stopped — leave the partial content as-is
      } else {
        setMessages((prev) => prev.map((m) =>
          m.id === asstId
            ? { ...m, content: (m.content ? m.content + '\n\n' : '') + `_Error: ${err.message}_` }
            : m
        ));
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const stop = () => {
    if (abortRef.current) abortRef.current.abort();
  };

  const hasMessages = messages.length > 0;

  return (
    <Drawer
      anchor="right" open={open} onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: { xs: '100%', sm: 460 },
            bgcolor: colors.surface,
            boxShadow: shadows.panel, borderLeft: 'none',
            display: 'flex', flexDirection: 'column',
          },
        },
      }}
    >
      {/* Header */}
      <Box sx={{
        p: '18px 22px 16px', borderBottom: `1px solid ${colors.canvas}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        flexShrink: 0,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{
            width: 32, height: 32, borderRadius: '10px',
            background: gradient.primary,
            display: 'grid', placeItems: 'center', color: '#fff',
            boxShadow: '0 2px 10px rgba(0,90,196,0.3)', position: 'relative',
            '&::after': {
              content: '""', position: 'absolute', inset: '-2px', borderRadius: '12px',
              border: '1.5px solid rgba(0,90,196,0.2)',
              animation: 'halo 2.4s ease-in-out infinite',
            },
            '@keyframes halo': {
              '0%,100%': { opacity: 0.6, transform: 'scale(1)' },
              '50%':     { opacity: 0, transform: 'scale(1.2)' },
            },
          }}>
            <AutoAwesomeIcon sx={{ fontSize: 16 }} />
          </Box>
          <Box>
            <Typography sx={{
              fontFamily: typography.fontFamilyDisplay, fontWeight: 700, fontSize: '14.5px',
              letterSpacing: '-0.005em', lineHeight: 1.2,
            }}>
              Case Assistant
            </Typography>
            <Box sx={{
              fontSize: '11px', color: colors.textSecondary, mt: 0.25,
              display: 'flex', gap: 0.75, alignItems: 'center',
            }}>
              <Box sx={{
                width: 6, height: 6, borderRadius: '50%',
                bgcolor: colors.success,
                boxShadow: `0 0 0 3px ${colors.successContainer}`,
              }} />
              Grounded on REF-{caseNumber}
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton size="small" sx={{ color: colors.textSecondary, borderRadius: '7px', '&:hover': { bgcolor: colors.canvas, color: colors.textPrimary } }}>
            <OpenInFullIcon sx={{ fontSize: 14 }} />
          </IconButton>
          <IconButton size="small" onClick={onClose} sx={{ color: colors.textSecondary, borderRadius: '7px', '&:hover': { bgcolor: colors.canvas, color: colors.textPrimary } }}>
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Thread */}
      <Box
        ref={scrollRef}
        sx={{
          flex: 1, overflowY: 'auto', p: '20px 22px 12px',
          display: 'flex', flexDirection: 'column', gap: 2.5,
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': { bgcolor: colors.canvas, borderRadius: 3 },
        }}
      >
        {!hasMessages && <EmptyState suggestions={suggestions} onPick={send} />}
        {messages.map((m) => {
          if (m.role === 'user') return <UserMessage key={m.id} content={m.content} />;
          const isLast = m.id === messages[messages.length - 1]?.id;
          return <AgentMessage key={m.id} content={m.content} streaming={streaming && isLast} />;
        })}
        {streaming && messages[messages.length - 1]?.content === '' && <TypingIndicator />}
      </Box>

      <Composer onSend={send} busy={streaming} onStop={stop} />
    </Drawer>
  );
};

export default AgentChatPanel;
